import data from '../content/pronunciation.json' with { type: 'json' };
import type { WordScore } from '../types.ts';
import { normalize } from './text.ts';

/**
 * Podpowiedzi „jak się to czyta”: zapis fonetyczny, wymowa „po polsku” (przybliżona) i krótkie rady do trudnych dla Polaków głosek.
 * Słownik pochodzi z `src/content/pronunciation.json` (ręcznie przygotowany, wymowa amerykańska). Rada do najsłabszej głoski bierze się
 * z oceny Azure (głoski każdego słowa), więc po próbie karta pokazuje to, co naprawdę wyszło słabo.
 */

interface Dict {
  words: Record<string, { ipa: string; pl: string; say?: string; focus: string[] }>;
  phonemes: Record<string, { ipa: string; tip: string }>;
}
const dict = data as unknown as Dict;

export interface WordTip {
  /** 'weak' = najsłabsza głoska z ostatniej próby; 'focus' = zwykle trudna dla Polaków głoska tego słowa */
  kind: 'weak' | 'focus';
  /** Klucz głoski ('r', 'dh', 'final-voiced'…) */
  key: string;
  /** Zapis głoski w IPA (puste dla rad o końcówkach) */
  ipa: string;
  /** Rada po polsku (z `pronunciation.json`). */
  text: string;
  /** Ta sama rada po angielsku (patch-33: interfejs idzie za przełącznikiem EN/PL; ekran wybiera `text` albo `textEn`). */
  textEn: string;
}

export interface WordHelp {
  word: string;
  ipa: string | null;
  /** Wymowa „po polsku” (przybliżenie) */
  pl: string | null;
  /** To samo co `pl`, ale z angielskim opisem w nawiasie (słowa „a” i „to”); reszta to zapis polskimi literami, który nie zależy od języka interfejsu. */
  plEn: string | null;
  tips: WordTip[];
}

/**
 * Rady po angielsku (klucz = głoska ze słownika). Polski tekst jest w `pronunciation.json`; tu jest jego angielska wersja dla trybu EN
 * (prosty angielski dla dziecka, które się go uczy; odwołania do polskich dźwięków zostają, bo to na nich dziecko się opiera).
 */
const TIPS_EN: Readonly<Record<string, string>> = {
  dh: 'th like in “this”: the tip of your tongue is lightly between your teeth, and your voice “buzzes”. It is not “z” or “d”.',
  th: 'th like in “think”: your tongue is lightly between your teeth and you only blow air, with no voice. It is not “f” or “s”.',
  w: 'Round your lips like for “oo”, and only then open them. It is not the Polish “w” (teeth on lip).',
  r: 'The English “r”: your tongue curls back, does not touch the roof of your mouth and does not shake. It is not the Polish “r”.',
  ae: 'Open your mouth wide, like “e” and “a” at the same time (like in “cat”). It is not just “e”.',
  ih: 'A short, relaxed “i”. Do not make it long — it is not “ii”.',
  iy: 'A long “iii”: stretch your lips like in a smile (like in “eat”).',
  uw: 'A long “uuu” with your lips pushed forward (like in “food”).',
  uh: 'A short, relaxed “u” (like in “good”). Do not make it long.',
  ng: '“ng” is one sound in the nose, with your tongue at the back. Do not add a clear “g” at the end.',
  hh: 'Just a quiet puff of air, softer than the Polish “ch”.',
  ax: 'A very short, weak vowel (like a very quiet “e”), for example “a” in “a book”.',
  er: '“er” with the English “r”: tongue at the back, no shaking.',
  ah: 'A short “a” from deep in your mouth, a bit between “a” and “o” (like in “cup”).',
  aa: 'A wide “a” from deep in your mouth (like in “want” and “father”).',
  ao: 'A round, open “o” (like in “water” and “door”).',
  ay: 'It starts with “a” and ends with “j”: “aj”.',
  ey: '“ej”: from “e” smoothly into “j”.',
  ow: '“ou”: from “o” smoothly into “u”. It is not the short Polish “o”.',
  aw: '“au”: from “a” smoothly into “u”.',
  y: '“j” like in the Polish word “jajko”.',
  'final-voiced': 'At the end of a word, “d”, “b”, “g”, “z” and “v” should still sound voiced — do not change them into “t”, “p”, “k”, “s” and “f”.',
  'final-stop': 'Finish the last sound (“t”, “k”, “p”): short and clear. Do not lose it at the end of the word.',
};

/** Angielski opis w nawiasie przy zapisie „po polsku” (tylko słowa, których zapis ma polski komentarz). */
const READING_EN: Readonly<Record<string, string>> = {
  a: 'e (short, quiet)',
  to: 'tuu (in a sentence often short: te)',
};

/** Słowa, po których słownik wie, jak się je czyta. */
export const knownWords = (): string[] => Object.keys(dict.words);

/** Klucz słownika dla słowa ze zdania (małe litery, bez apostrofów na brzegach). */
export const wordKey = (word: string): string => normalize(word).replace(/^'+|'+$/g, '');

/** Tekst wysyłany do syntezy mowy dla pojedynczego słowa (zwykle samo słowo; słownik może go poprawić polem `say`, np. „a” → „uh”). */
export const spokenForm = (word: string): string => dict.words[wordKey(word)]?.say ?? (wordKey(word) || word);

/** Ogranicza liczbę rad na karcie — dziecko ma czytać, nie przewijać. */
export const MAX_TIPS = 3;
/** Najsłabszą głoskę wskazujemy tylko, gdy faktycznie wypadła słabo. */
export const WEAK_PHONEME_BELOW = 85;

const VOICED_FINAL = new Set(['d', 'b', 'g', 'z', 'v']);
const STOP_FINAL = new Set(['t', 'k', 'p']);

function tipFor(key: string, kind: WordTip['kind']): WordTip | null {
  const p = dict.phonemes[key];
  return p ? { kind, key, ipa: p.ipa, text: p.tip, textEn: TIPS_EN[key] ?? p.tip } : null;
}

/**
 * Karta pomocy dla słowa. `scored` (ocena tego słowa z ostatniej próby) jest opcjonalne: gdy jest, pierwsza rada dotyczy najsłabszej głoski.
 * Dla słowa spoza słownika zwraca kartę bez zapisu (same rady z oceny, jeśli są).
 */
export function wordHelp(word: string, scored?: WordScore | null): WordHelp {
  const key = wordKey(word);
  const entry = dict.words[key];
  const tips: WordTip[] = [];
  const phonemes = scored?.phonemes ?? [];
  if (phonemes.length > 0) {
    let weakest = phonemes[0]!;
    for (const p of phonemes) if (p.accuracy < weakest.accuracy) weakest = p;
    if (weakest.accuracy < WEAK_PHONEME_BELOW) {
      const last = phonemes[phonemes.length - 1]!;
      // słaba ostatnia głoska to najczęściej zgubiona końcówka — wtedy ważniejsza jest rada o końcówce niż o samej głosce
      const endKey = weakest === last ? (VOICED_FINAL.has(last.phoneme) ? 'final-voiced' : STOP_FINAL.has(last.phoneme) ? 'final-stop' : null) : null;
      const tip = tipFor(endKey ?? weakest.phoneme, 'weak') ?? { kind: 'weak' as const, key: weakest.phoneme, ipa: '', text: `Spróbuj wolniej, osobno tę głoskę („${weakest.phoneme}”).`, textEn: `Try it slower, and say this sound on its own (“${weakest.phoneme}”).` };
      tips.push(tip);
    }
  }
  for (const k of entry?.focus ?? []) {
    if (tips.length >= MAX_TIPS) break;
    if (tips.some((t) => t.key === k)) continue;
    const t = tipFor(k, 'focus');
    if (t) tips.push(t);
  }
  return { word: key || word, ipa: entry?.ipa ?? null, pl: entry?.pl ?? null, plEn: entry ? (READING_EN[key] ?? entry.pl) : null, tips };
}
